import { Collection } from 'firestorter';
import { firestorePaths } from '../utils/paths';
import BaseDocument from './BaseDocument';

/**
 * Status of the subscription
 */
const subscriptionStatus = {
  ACTIVE: 'ACTIVE',
  TRIALING: 'TRIALING',
  PAST_DUE: 'PAST_DUE',
  INCOMPLETE: 'INCOMPLETE',
  INCOMPLETE_EXPIRED: 'INCOMPLETE_EXPIRED',
  CANCELED: 'CANCELED',
  UNPAID: 'UNPAID',
};

const UnpaidStatuses = [
  subscriptionStatus.PAST_DUE,
  subscriptionStatus.INCOMPLETE,
  subscriptionStatus.UNPAID,
];

const CanceledStatuses = [
  subscriptionStatus.CANCELED,
  subscriptionStatus.INCOMPLETE_EXPIRED,
];

/**
 * A Stripe subscription's current period end, from whichever shape carries it.
 * Newer Stripe API versions moved `current_period_end` onto the subscription items;
 * older mirror documents still carry it at the top level.
 * @param {Object} stripeSubscription - The mirrored Stripe subscription.
 * @return {number|undefined} The period end in epoch seconds.
 */
const getCurrentPeriodEnd = (stripeSubscription) => (
  stripeSubscription?.items?.data?.[0]?.current_period_end ?? stripeSubscription?.current_period_end
);

/**
 * Class representing a Subscription associated to a User.
 *
 * @class Subscription
 * @extends BaseDocument
 */
class Subscription extends BaseDocument {
  constructor(id, opts) {
    super(`${firestorePaths.STRIPE_SUBSCRIPTIONS}/${id}`, opts);
  }

  /**
   * Get all of the subscription data as an object.
   * @return {Object}
   */
  get subscriptionData() {
    return this.data.stripeSubscription;
  }

  /**
   * Get the associated stripe account ID.
   * @return {string}
   */
  get stripeAccountId() {
    return this.data.stripeAccountId;
  }

  /**
   * The current period end (next billing date) in epoch seconds.
   * @return {number|undefined}
   */
  get currentPeriodEnd() {
    return getCurrentPeriodEnd(this.subscriptionData);
  }

  /**
   * The contract this subscription was created for, carried in Stripe metadata.
   * The only reliable link between a subscription and a product now that a client
   * may hold several subscriptions with one coach.
   * @return {string|undefined}
   */
  get contractId() {
    return this.subscriptionData?.metadata?.contractId;
  }

  /**
   * Live query for the user's current subscription document(s). Prefer this when the UI should
   * update as Firestore changes; pair with a MobX `autorun` on `collection.docs` (see BillingDetails).
   * @param {string} userId
   * @return {Promise<Collection>}
   */
  static async getCurrentSubscriptionsCollectionForUser(userId) {
    const subscriptionsCollection = new Collection(firestorePaths.STRIPE_SUBSCRIPTIONS,
      {
        createDocument: ({ id }, opts) => new Subscription(id, opts),
        query: (ref) => ref
          .where('user', '==', userId)
          .where('current', '==', true),
      });
    await Subscription.initCollection(subscriptionsCollection);
    return subscriptionsCollection;
  }

  /**
   * Returns the current subscription by a given user ID.
   * @param {string} userId - The ID of the user.
   *  @return {Promise<Subscription|void>} A promise that resolves to a Subscription, or null if it doesn't exist.
   */
  static async getCurrentSubscription(userId) {
    const subscriptionsCollection = await Subscription.getCurrentSubscriptionsCollectionForUser(userId);

    if (!subscriptionsCollection.hasDocs) {
      return null;
    }

    const doc = subscriptionsCollection.docs[0];
    return doc;
  }

  /**
   * The user's current subscription for ONE contract. Several can be current at once, one
   * per product, so `getCurrentSubscription` above returns an arbitrary one.
   *
   * @param {string} userId - The ID of the user.
   * @param {string} contractId - The contract whose subscription is wanted.
   * @return {Promise<Subscription|null>} The subscription, or null when the answer is ambiguous.
   */
  static async getCurrentSubscriptionByContractId(userId, contractId) {
    if (!contractId) {
      return null;
    }
    const subscriptionsCollection = await Subscription.getCurrentSubscriptionsCollectionForUser(userId);
    const named = subscriptionsCollection.docs.find((doc) => doc.contractId === contractId);
    if (named) {
      return named;
    }

    const withoutContract = subscriptionsCollection.docs.filter((doc) => !doc.contractId);
    return (subscriptionsCollection.docs.length === 1 && withoutContract.length === 1) ? withoutContract[0] : null;
  }
}

export default Subscription;

export {
  getCurrentPeriodEnd,
  subscriptionStatus,
  UnpaidStatuses,
  CanceledStatuses,
};
